'use client';

import { useEffect, useRef } from 'react';

import { cn } from '@eliadmin/core/src/utils/style-utils';

interface Props {
  children: React.ReactNode;
  width?: 'fit-content' | 'full';
  variant: 'left' | 'right' | 'top' | 'bottom';
  className?: string;
}

const revealTransforms: Record<Props['variant'], string> = {
  left: 'translateX(-50px)',
  right: 'translateX(50px)',
  top: 'translateY(-50px)',
  bottom: 'translateY(50px)',
};

export const Reveal = ({ children, width = 'full', className, variant = 'left' }: Props) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element || !('IntersectionObserver' in window) || !element.animate) return;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reducedMotion.matches) return;

    let firstObservation = true;
    let animation: Animation | undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        // Never hide the first viewport while waiting for hydration/animation.
        // SSR/no-JS content remains visible; only later scroll entries animate.
        if (!firstObservation && !reducedMotion.matches) {
          animation = element.animate(
            [
              { opacity: 0, transform: revealTransforms[variant] },
              { opacity: 1, transform: 'none' },
            ],
            { duration: 600, easing: 'cubic-bezier(0.25, 0.1, 0.25, 1)' },
          );
        }
        observer.disconnect();
      }
      firstObservation = false;
    });
    const onMotionChange = () => {
      if (reducedMotion.matches) {
        animation?.cancel();
        observer.disconnect();
      }
    };
    observer.observe(element);
    reducedMotion.addEventListener('change', onMotionChange);
    return () => {
      observer.disconnect();
      animation?.cancel();
      reducedMotion.removeEventListener('change', onMotionChange);
    };
  }, [variant]);

  return (
    <div className={cn('relative', width === 'full' ? 'w-full' : 'w-fit', className)}>
      <div ref={ref}>{children}</div>{' '}
    </div>
  );
};
